import { isArray } from 'lodash';
import { useCallback } from 'react';
import { URLSearchParamsInit } from 'react-router-dom';
import useSearchParamsWithSetInit from './useSearchParamsWithSetInit';

type DynamicParamsFunctions<Params> = {
  params: URLSearchParams;
  updateParams(params: Partial<Params>): void;
  removeParams(keys: keyof Params | (keyof Params)[]): void;
  removeParamValue<K extends keyof Params>(
    key: K,
    value?: Params[K] extends Array<infer U> ? U : Params[K],
  ): void;
};

export function useDynamicParams<Params>(
  defaultParams?: URLSearchParamsInit,
): DynamicParamsFunctions<Params> {
  const [params, setParams] = useSearchParamsWithSetInit(defaultParams);

  type ArrayElement<T> = T extends Array<infer U> ? U : never;

  const updateParams = useCallback(
    (params: Partial<Params>) => {
      setParams(prev => {
        const nextParams = new URLSearchParams(prev);
        for (const key in params) {
          const value = params[key];
          nextParams.delete(key);

          if (!value) continue;

          if (isArray(value)) {
            value.forEach((v: ArrayElement<Params[typeof key]>) => {
              nextParams.append(key, String(v));
            });
          } else {
            nextParams.append(key, String(value));
          }
        }
        return nextParams;
      });
    },
    [setParams],
  );

  const removeParams = useCallback(
    (keys: keyof Params | (keyof Params)[]) => {
      setParams(prev => {
        const nextParams = new URLSearchParams(prev);
        if (isArray(keys)) {
          keys.forEach(key => nextParams.delete(String(key)));
        } else {
          nextParams.delete(keys as string);
        }
        return nextParams;
      });
    },
    [setParams],
  );

  const removeParamValue = useCallback(
    <K extends keyof Params>(
      key: K,
      value?: Params[K] extends Array<infer U> ? U : Params[K],
    ) => {
      setParams(prev => {
        const nextParams = new URLSearchParams(prev);
        const values = nextParams.getAll(String(key));
        if (values && value) {
          const filteredValues = values.filter(
            filterValue => String(value) !== filterValue,
          );
          nextParams.delete(String(key));
          filteredValues.forEach(filteredValue =>
            nextParams.append(String(key), filteredValue),
          );
        }
        return nextParams;
      });
    },
    [setParams],
  );

  return {
    params,
    updateParams,
    removeParams,
    removeParamValue,
  };
}
